import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { formatPrice, imgUrl } from "../lib/api";

export const QtyControl = ({ value, onChange, max = 20, testid }) => (
  <div className="inline-flex items-center border border-[#C9A45C]/30" data-testid={testid}>
    <button type="button" className="h-9 w-9 flex items-center justify-center hover:text-[#C9A45C] disabled:opacity-30" onClick={() => onChange(value - 1)} disabled={value <= 1} aria-label="Diminuer" data-testid={`${testid}-minus`}>
      <Minus className="h-3.5 w-3.5" />
    </button>
    <span className="w-9 text-center text-sm" data-testid={`${testid}-value`}>{value}</span>
    <button type="button" className="h-9 w-9 flex items-center justify-center hover:text-[#C9A45C] disabled:opacity-30" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label="Augmenter" data-testid={`${testid}-plus`}>
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

export const CartDrawer = () => {
  const { items, open, setOpen, update, remove, subtotal } = useCart();
  const nav = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#0B0B0B] border-[#C9A45C]/20 w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <div className="p-6 border-b border-[#C9A45C]/15">
          <SheetTitle className="font-display text-3xl font-normal text-[#F8F6F1]">Votre panier</SheetTitle>
        </div>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center" data-testid="cart-empty">
            <ShoppingBag className="h-10 w-10 text-[#C9A45C]/60" />
            <p className="text-[#F8F6F1]/60">Votre panier est vide.</p>
            <button className="btn-outline-gold" onClick={() => { setOpen(false); nav("/boutique"); }} data-testid="cart-empty-shop-button">Découvrir nos parfums</button>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto divide-y divide-[#C9A45C]/10 px-6">
              {items.map((i) => (
                <li key={i.product_id} className="py-5 flex gap-4" data-testid={`cart-item-${i.slug}`}>
                  <Link to={`/parfum/${i.slug}`} onClick={() => setOpen(false)} className="shrink-0">
                    <img src={imgUrl(i.image)} alt={i.name} className="h-24 w-20 object-cover bg-[#141414]" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight truncate">{i.name}</p>
                    <p className="text-xs text-[#F8F6F1]/50 mt-1">{i.volume_ml > 0 ? `${i.volume_ml} ml · ` : ""}{formatPrice(i.price)}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <QtyControl value={i.quantity} max={Math.min(i.stock || 20, 20)} onChange={(q) => update(i.product_id, q)} testid={`cart-qty-${i.slug}`} />
                      <button onClick={() => remove(i.product_id)} className="p-2 text-[#F8F6F1]/50 hover:text-red-400 transition-colors" aria-label="Supprimer" data-testid={`cart-remove-${i.slug}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="p-6 border-t border-[#C9A45C]/15 space-y-4 bg-[#0e0e0e]">
              <div className="flex justify-between items-baseline">
                <span className="eyebrow">Sous-total</span>
                <span className="font-display text-2xl text-[#C9A45C]" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <p className="text-xs text-[#F8F6F1]/45">Livraison et codes promo calculés à l'étape suivante.</p>
              <button className="btn-gold w-full" onClick={() => { setOpen(false); nav("/commande"); }} data-testid="cart-checkout-button">Valider la commande</button>
              <button className="btn-outline-gold w-full" onClick={() => setOpen(false)} data-testid="cart-continue-shopping-button">Continuer mes achats</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
